export const FORM_TYPES = {
  GAS_SAFETY: 'gas-safety-record',
  ELECTRICAL: 'electrical-certificate'
};

export const FORM_STATUS = {
  DRAFT: 'draft',
  COMPLETED: 'completed',
  ARCHIVED: 'archived',
  ISSUED: 'issued'
};

/**
 * Customer / Property Asset CRM enums (Track 5)
 */

// Customer types per tasks.md Track 5.1
export const CUSTOMER_TYPES = {
  HOMEOWNER: 'homeowner',
  LANDLORD: 'landlord',
  ESTATE_AGENT: 'estate_agent',
  COMMERCIAL: 'commercial_contractor'
};

export const CUSTOMER_TYPE_LABELS = {
  [CUSTOMER_TYPES.HOMEOWNER]: 'Homeowner',
  [CUSTOMER_TYPES.LANDLORD]: 'Landlord',
  [CUSTOMER_TYPES.ESTATE_AGENT]: 'Estate Agent',
  [CUSTOMER_TYPES.COMMERCIAL]: 'Commercial Contractor'
};

// Property appliance registry enums per tasks.md Track 5.2
export const APPLIANCE_TYPES = {
  BOILER: 'Boiler',
  FIRE: 'Fire',
  COOKER: 'Cooker',
  WATER_HEATER: 'Water Heater',
  HEAT_PUMP: 'Heat Pump',
  SOLAR_THERMAL: 'Solar Thermal',
  HOB: 'Hob',
  OVEN: 'Oven',
  OTHER: 'Other'
};

export const FLUE_TYPES = {
  OPEN_FLUE: 'Open Flue',
  ROOM_SEALED: 'Room Sealed',
  FLUELESS: 'Flueless',
  CONVENTIONAL: 'Conventional Flue',
  NOT_APPLICABLE: 'N/A'
};

export const APPLIANCE_STATUS = {
  ACTIVE: 'active',
  DECOMMISSIONED: 'decommissioned'
};

// NOTE: the canonical "Local Workspace" tenant id lives in
// src/lib/database/index.js as LOCAL_WORKSPACE_TENANT_ID.
// Import it from there instead of duplicating the value here.

export const SAFETY_CHECK_RESULTS = {
  SATISFACTORY: 'Satisfactory',
  NOT_SATISFACTORY: 'Not Satisfactory',
  NOT_TESTED: 'Not Tested'
};

export const CIRCUIT_TYPES = {
  LIGHTING: 'Lighting',
  POWER: 'Power',
  COOKER: 'Cooker',
  SHOWER: 'Shower',
  IMMERSION_HEATER: 'Immersion Heater',
  OTHER: 'Other'
};

export const PROTECTIVE_DEVICES = {
  RCBO: 'RCBO',
  RCD: 'RCD',
  MCB: 'MCB',
  FUSE: 'Fuse'
};

/**
 * Tenant trade & team management enums (Dev 2 — Tenant Trade Gating)
 * Trade values map 1:1 to the `category` field on entries in src/forms/index.js.
 */

export const TRADES = {
  ELECTRICAL: 'Electrical',
  GAS_SAFETY: 'Gas Safety',
  OIL: 'Oil',
  PLUMBING: 'Plumbing',
  HVAC: 'HVAC / Renewables'
};

export const ALL_TRADES = Object.values(TRADES);

// UI metadata for trade selectors / admin toggles
export const TRADE_META = {
  [TRADES.ELECTRICAL]: {
    label: 'Electrical',
    icon: '⚡',
    description: 'EIC, EICR, Minor Works (MEIWC) & PAT testing certificates',
    accreditations: ['NICEIC', 'NAPIT']
  },
  [TRADES.GAS_SAFETY]: {
    label: 'Gas Safety',
    icon: '🔥',
    description: 'CP12, Gas Warning, Mobile Catering, LPG, Caravan & Boat records',
    accreditations: ['Gas Safe']
  },
  [TRADES.OIL]: {
    label: 'Oil Firing (OFTEC)',
    icon: '🛢️',
    description: 'OFTEC oil installation, servicing & commissioning reports',
    accreditations: ['OFTEC']
  },
  [TRADES.PLUMBING]: {
    label: 'Plumbing',
    icon: '💧',
    description: 'Unvented cylinders, commissioning & decommissioning records',
    accreditations: []
  },
  [TRADES.HVAC]: {
    label: 'HVAC / Renewables',
    icon: '♨️',
    description: 'Heat pumps, CHP, TMVs, solar thermal & renewable commissioning',
    accreditations: ['MCS']
  }
};

// Team member roles
export const ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  ENGINEER: 'engineer'
};

export const ROLE_LABELS = {
  [ROLES.OWNER]: 'Owner',
  [ROLES.ADMIN]: 'Admin',
  [ROLES.ENGINEER]: 'Engineer'
};

export const ROLE_BADGE_CLASSES = {
  [ROLES.OWNER]: 'bg-purple-100 dark:bg-purple-950/50 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-900',
  [ROLES.ADMIN]: 'bg-blue-100 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-900',
  [ROLES.ENGINEER]: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700'
};

// Accreditations & their license-number registration fields
export const ACCREDITATIONS = {
  GAS_SAFE: 'Gas Safe',
  NICEIC: 'NICEIC',
  OFTEC: 'OFTEC',
  NAPIT: 'NAPIT',
  MCS: 'MCS'
};

export const ACCREDITATION_FIELDS = {
  [ACCREDITATIONS.GAS_SAFE]: {
    label: 'Gas Safe Registration No.',
    placeholder: 'e.g. 654321',
    field: 'gasSafeRegNumber'
  },
  [ACCREDITATIONS.NICEIC]: {
    label: 'NICEIC Approval No.',
    placeholder: 'e.g. 604209-000',
    field: 'niceicNumber'
  },
  [ACCREDITATIONS.OFTEC]: {
    label: 'OFTEC Registration No.',
    placeholder: 'e.g. C8041',
    field: 'oftecRegNumber'
  },
  [ACCREDITATIONS.NAPIT]: {
    label: 'NAPIT Membership No.',
    placeholder: 'e.g. 30245',
    field: 'napitNumber'
  },
  [ACCREDITATIONS.MCS]: {
    label: 'MCS Certification No.',
    placeholder: 'e.g. MCS-66120',
    field: 'mcsNumber'
  }
};